import { CREDIT_CARD_ERROR_MAP } from "app/utils/form/validators";
import {
	CITY_MAX_CHARS,
	CIVILITY,
	CREDIT_CARDS_TYPES,
	FIRST_NAME_LIMIT_CHARS,
	LAST_NAME_LIMIT_CHARS,
	NAME_POLICY_REGEX,
	PAYMENT_METHODS,
} from "app/constants";
import { determineCardType, validateCreditCardData } from "app/utils/creditCardUtils";
import { string, object, array, boolean } from "yup";
import { validatePhoneIntl } from "app/utils/utils";
import { isEmpty } from "lodash";
import forOwn from "lodash/forOwn";

export const dependantPaymentCardFields = ["expiryMonth", "expiryYear", "cvc", "number"];
export const dependantPassengersNameFields = ["lastName", "firstName"];

export const defaultValues = {
	contact: {
		"honorific-prefix": "",
		"family-name": "",
		"given-name": "",
		"address-line1": "",
		"address-line2": "",
		"postal-code": "",
		"address-level2": "", // city
		"country-name": "",
		phone: "",
		email: "",
	},
	passengers: [
		{
			type: "adult",
			index: 1,
			name: {
				gender: "",
				lastName: "",
				firstName: "",
			},
			birthdate: { day: "", month: "", year: "" },
			frequentFlyerTravellerCardNumber: "",
		},
		{
			type: "adult",
			index: 2,
			name: {
				gender: "",
				lastName: "",
				firstName: "",
			},
			birthdate: { day: "", month: "", year: "" },
			frequentFlyerTravellerCardNumber: "",
		},
	],
	coupon: "",
	insurance: "",
	terms: false,
	payment: {
		mode: "",
		type: "",
	},
};

const contactSchema = object().shape({
	"honorific-prefix": string().required({ id: "error.gender.required" }),
	"family-name": string()
		.required({ id: "error.firstName.required" })
		.min(FIRST_NAME_LIMIT_CHARS, { id: "error.firstName.minlength" })
		.test(
			"name-policy-char",
			{ id: "error.firstName.invalid" },
			value =>
				!(NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) || NAME_POLICY_REGEX.DIGIT.test(value))
		),

	"given-name": string()
		.required({ id: "error.lastName.required" })
		.min(LAST_NAME_LIMIT_CHARS, { id: "error.lastName.minlength" })
		.test(
			"name-policy-char",
			{ id: "error.lastName.invalid" },
			value =>
				!(NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) || NAME_POLICY_REGEX.DIGIT.test(value))
		),
	"address-line1": string().required({ id: "error.address.line1.required" }),
	"postal-code": string().required({ id: "error.address.postCode.required" }),
	"address-level2": string()
		.required({ id: "error.address.city.required" })
		.max(CITY_MAX_CHARS, { id: "error.lastName.minlength" }),
	"country-name": string().required({ id: "error.address.country.required" }),
	// La validation automatique des e-mail avec Yup ne fonctionne pas correctement. Il y a un issue ouvert à ce sujet depuis 2019 https://github.com/jquense/yup/issues/507#issuecomment-765799429.
	// il faut utiliser le regex
	email: string()
		.required({ id: "error.email.required" })
		.test("email", { id: "error.email.format" }, value =>
			/^(([^<>()\\.,;:\s@"]+(\.[^<>()\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
				value
			)
		),
	phone: string()
		.required({ id: "error.phone.required" })
		.test("phone-check", { id: "error.phone.invalid" }, value => validatePhoneIntl(value)),
});

const passengersSchema = dateOfBirthRequired =>
	array().of(
		object().shape({
			name: object().shape({
				gender: string().required({ id: "error.gender.required" }),
				lastName: string()
					.required({ id: "error.lastName.required" })
					.min(LAST_NAME_LIMIT_CHARS, { id: "error.lastName.minlength" })
					.test("unique-full-name", { id: "error.passengers.unique.invalid" }, function(
						value,
						context
					) {
						const passengers =
							context?.from[context?.from?.length - 1]?.value?.passengers;

						// Accès aux autres valeurs du schéma via context.parent
						const { firstName, lastName } = context?.parent;
						const path = context.path;
						const index = Number(path.charAt(path.indexOf("[") + 1));
						const type = passengers[`${index}`].type;
						//  Vérification de l'unicité
						return !passengers.some((passenger, idx) => {
							if (
								typeof passenger?.name?.firstName === "string" &&
								typeof passenger?.name?.lastName === "string" &&
								typeof firstName === "string" &&
								typeof lastName === "string"
							) {
								return (
									idx !== index &&
									passenger.name?.firstName.toLowerCase() ===
										firstName.toLowerCase() &&
									passenger.name.lastName.toLowerCase() ===
										lastName.toLowerCase() &&
									passenger.type === type &&
									passenger.index < index + 1
								);
							}
							return false;
						});
					})
					.test(
						"name-policy-char",
						{ id: "error.lastName.invalid" },
						value =>
							!(
								NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) ||
								NAME_POLICY_REGEX.DIGIT.test(value)
							)
					),
				firstName: string()
					.required({ id: "error.firstName.required" })
					.min(FIRST_NAME_LIMIT_CHARS, { id: "error.firstName.minlength" })
					.test("unique-full-name", { id: "error.passengers.unique.invalid" }, function(
						value,
						context
					) {
						const passengers =
							context?.from[context?.from?.length - 1]?.value?.passengers;

						// Accès aux autres valeurs du schéma via context.parent
						const { firstName, lastName } = context?.parent;
						const path = context.path;
						const index = Number(path.charAt(path.indexOf("[") + 1));
						const type = passengers[`${index}`].type;
						//  Vérification de l'unicité
						return !passengers.some((passenger, idx) => {
							if (
								typeof passenger?.name?.firstName === "string" &&
								typeof passenger?.name?.lastName === "string" &&
								typeof firstName === "string" &&
								typeof lastName === "string"
							) {
								return (
									idx !== index &&
									passenger.name.firstName.toLowerCase() ===
										firstName.toLowerCase() &&
									passenger.name.lastName.toLowerCase() ===
										lastName.toLowerCase() &&
									passenger.type === type &&
									passenger.index < index + 1
								);
							}
							return false;
						});
					})
					.test(
						"name-policy-char",
						{ id: "error.firstName.invalid" },
						value =>
							!(
								NAME_POLICY_REGEX.SPECIAL_CHAR.test(value) ||
								NAME_POLICY_REGEX.DIGIT.test(value)
							)
					),
			}),
			birthdate: dateOfBirthRequired
				? object().shape({
						day: string().required({ id: "error.birthdate.day" }),
						month: string().required({ id: "error.birthdate.month" }),
						year: string().required({ id: "error.birthdate.year" }),
				  })
				: object().shape({}),
		})
	);
const insuranceSchema = string().required({ id: "error.payment.insurance.required" });

const termsSchema = boolean().oneOf([true], { id: "error.must.accept.terms" });

const paymentSchema = (
	selectedPaymentTypeValue,
	acceptedCreditCardTypes,
	cartAmount,
	showAmountToPay
) => {
	let paymentValidators = {};

	if (selectedPaymentTypeValue === PAYMENT_METHODS.CASINO) {
		paymentValidators = {
			card: object().shape({
				maidenName: string().test(
					"maiden-name-validation",
					{ id: "error.payment.maiden.name.required" },
					function(value, context) {
						const honorificPrefix =
							context?.from[context?.from?.length - 1]?.value?.contact[
								"honorific-prefix"
							];
						if (honorificPrefix !== CIVILITY.MALE) {
							return !isEmpty(value);
						}

						return true;
					}
				),

				zipCodeOfBirth: string()
					.required({ id: "error.payment.zip.code.of.birth.required" })
					.min(2, { id: "error.departement.code.required" }),
				birthdate: object().shape({
					day: string().required({ id: "error.birthdate.day" }),
					month: string().required({ id: "error.birthdate.month" }),
					year: string().required({ id: "error.birthdate.year" }),
				}),
				holderName: string().required({ id: "error.payment.card.holder.name.required" }),
				number: string()
					.test("number-required", { id: "error.payment.card.number.required" }, function(
						value
					) {
						if (determineCardType(value) !== CREDIT_CARDS_TYPES.MAESTRO && !value) {
							return false;
						}
						return true;
					})
					.test(
						"is-card-type-accepted",
						{ id: "error.payment.credit.card.type.not.accepted.new" },
						function(value) {
							const creditCardType = determineCardType(value);
							return acceptedCreditCardTypes?.includes(creditCardType);
						}
					)
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function(value) {
							const payment = this.parent;
							const creditCardData = {
								number: value,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryMonth: string()
					.required({
						id: "error.payment.card.expiryMonth.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);

							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryYear: string()
					.required({
						id: "error.payment.card.expiryYear.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				cvc: string()
					.required({ id: "error.payment.card.cvc.required" })
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.CARD) {
		paymentValidators = {
			card: object().shape({
				amount: showAmountToPay
					? string().test(
							"is-card-type-accepted",
							{ id: "error.payment.card.amount.format" },
							function() {
								const creditCardType = determineCardType(this.parent?.number);
								return acceptedCreditCardTypes?.includes(creditCardType);
							}
					  )
					: undefined,
				holderName: string().required({
					id: "error.payment.card.holder.name.required",
				}),
				number: string()
					.test("number-required", { id: "error.payment.card.number.required" }, function(
						value
					) {
						if (determineCardType(value) !== CREDIT_CARDS_TYPES.MAESTRO && !value) {
							return false;
						}
						return true;
					})
					.test(
						"is-card-type-accepted",
						{ id: "error.payment.credit.card.type.not.accepted.new" },
						function(value) {
							const creditCardType = determineCardType(value);
							return acceptedCreditCardTypes?.includes(creditCardType);
						}
					)
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function(value) {
							const payment = this.parent;
							const creditCardData = {
								number: value,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryMonth: string()
					.required({
						id: "error.payment.card.expiryMonth.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);

							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryYear: string()
					.required({
						id: "error.payment.card.expiryYear.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				cvc: string()
					.test("valid-cvc", { id: "error.payment.card.cvc.required" }, function(value) {
						const shouldValidateCVC =
							determineCardType(this.parent?.number) !== CREDIT_CARDS_TYPES.MAESTRO;

						if (shouldValidateCVC) {
							return !isEmpty(value);
						}
						return true;
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								cvc: payment?.cvc,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					), //
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.BCMC) {
		paymentValidators = {
			cardBCMC: object().shape({
				holderName: string().required({ id: "error.payment.card.holder.name.required" }),
				number: string()
					.test("number-required", { id: "error.payment.card.number.required" }, function(
						value
					) {
						if (determineCardType(value) !== CREDIT_CARDS_TYPES.MAESTRO && !value) {
							return false;
						}
						return true;
					})
					.test(
						"is-card-type-accepted",
						{ id: "error.payment.credit.card.type.not.accepted.new" },
						function(value) {
							const creditCardType = determineCardType(value);
							if (!["bcmc"].includes(creditCardType)) {
								return false;
							}
							return true;
						}
					)
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.BCMC.invalid" },
						function(value) {
							const payment = this.parent;
							let creditCardData = {
								number: value,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryMonth: string()
					.required({
						id: "error.payment.card.expiryMonth.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const payment = this.parent;
							const creditCardData = {
								number: payment?.number,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};
							let adyenErrors = validateCreditCardData(creditCardData);

							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
				expiryYear: string()
					.required({
						id: "error.payment.card.expiryYear.required",
					})
					.test(
						"valid-credit-card",
						{ id: "error.payment.credit.card.invalid" },
						function() {
							const { payment } = this.parent;
							const creditCardData = {
								number: payment?.number,
								expiryMonth: payment?.expiryMonth,
								expiryYear: "20" + payment?.expiryYear,
								holderName: payment?.holderName,
							};

							let adyenErrors = validateCreditCardData(creditCardData);
							let errors = {};

							forOwn(adyenErrors, (value, key) => {
								if (value === false) {
									errors[key] = CREDIT_CARD_ERROR_MAP[key];
								}
							});

							return isEmpty(errors);
						}
					),
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.IDEAL) {
		paymentValidators = {
			issuerId: string().required({ id: "error.payment.issuerId.required" }),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.SWISS_BILLING) {
		paymentValidators = {
			swissBilling: object().shape({
				birthdate: object().shape({
					day: string().required({ id: "error.birthdate.day" }),
					month: string().required({ id: "error.birthdate.month" }),
					year: string().required({ id: "error.birthdate.year" }),
				}),
				invoiceType: string().required({ id: "error.payment.invoiceType.required" }),
			}),
		};
	}
	if (cartAmount > 0) {
		return object().shape(paymentValidators);
	}
	return object().shape({});
};

export const validationSchema = (
	selectedPaymentTypeValue,
	acceptedCreditCardTypes,
	cartAmount,
	dateOfBirthRequired,
	showAmountToPay
) =>
	object().shape({
		contact: contactSchema,
		passengers: passengersSchema(dateOfBirthRequired),
		insurance: insuranceSchema,
		terms: termsSchema,
		payment: paymentSchema(
			selectedPaymentTypeValue,
			acceptedCreditCardTypes,
			cartAmount,
			showAmountToPay
		),
	});
